<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { usePayoutAccount, claimRefreshRedirect } from '@/composables/usePayoutAccount'
import type { PayoutAccountErrorKind } from '@/composables/usePayoutAccount'
import { PayoutAccountStatus, PayoutLinkPurpose } from '@/types/payouts'
import type { PayoutAccount } from '@/types/payouts'
import type { Organisation } from '@/types/auth'
import type { OrganisationWriteError } from '@/types/organisation-settings'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import PayoutExplanation from '@/components/organizer/payouts/PayoutExplanation.vue'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const { user } = useAuth()

const props = defineProps<{
  organisation: Organisation
  orgId: number
  // Passed via shared RouterView slot - unused by this panel
  loading?: boolean
  formErrors?: Record<string, string[]>
  logoErrors?: string[]
  settingsError?: OrganisationWriteError | null
  logoActionError?: OrganisationWriteError | null
  saved?: boolean
  isOwner?: boolean
}>()

// Payments panel does not emit save/cancel/uploadLogo/deleteLogo - defined to
// satisfy the shared RouterView slot's event listeners without TypeScript errors.
defineEmits<{
  save: [payload: Record<string, unknown>]
  cancel: []
  uploadLogo: [file: File]
  deleteLogo: []
}>()

const isOrgOwner = computed<boolean>(() => props.organisation.role === 'owner')

const {
  refreshing,
  redirecting,
  error: flowError,
  refresh,
  startHostedFlow,
} = usePayoutAccount(computed(() => props.orgId))

// The auth store is the live source (refresh writes back into it); the
// organisation prop is the fallback until the store entry is available.
const account = computed<PayoutAccount>(() => {
  const fromStore = user.value?.organisations.find((o) => o.id === props.orgId)?.payout_account
  return fromStore ?? props.organisation.payout_account
})

const status = computed<PayoutAccountStatus>(() => account.value.status)

type Phase = 'idle' | 'returning' | 'error'
const phase = ref<Phase>('idle')
const flowErrorKind = ref<PayoutAccountErrorKind | null>(null)
const retryPurpose = ref<PayoutLinkPurpose | null>(null)

const busy = computed<boolean>(() => redirecting.value || refreshing.value || phase.value === 'returning')

const dueDate = computed<string | null>(() => {
  const iso = account.value.requirements_due_at
  if (!iso) return null
  return new Intl.DateTimeFormat(locale.value, { dateStyle: 'medium' }).format(new Date(iso))
})

const statusTitle = computed<string>(() => {
  switch (status.value) {
    case PayoutAccountStatus.NotStarted:
      return t('organizer.organisation.payments.status_not_started')
    case PayoutAccountStatus.Incomplete:
      return t('organizer.organisation.payments.status_incomplete_title')
    case PayoutAccountStatus.PendingVerification:
      return t('organizer.organisation.payments.status_pending_title')
    case PayoutAccountStatus.Active:
      return t('organizer.organisation.payments.status_active_title')
    case PayoutAccountStatus.ActionRequired:
      return t('organizer.organisation.payments.status_action_title')
    default:
      return t('organizer.organisation.payments.status_restricted_title')
  }
})

const statusBody = computed<string | null>(() => {
  switch (status.value) {
    case PayoutAccountStatus.Incomplete:
      return t('organizer.organisation.payments.status_incomplete_body')
    case PayoutAccountStatus.PendingVerification:
      return t('organizer.organisation.payments.status_pending_body')
    case PayoutAccountStatus.Active:
      return t('organizer.organisation.payments.status_active_body')
    case PayoutAccountStatus.ActionRequired:
      return dueDate.value !== null
        ? t('organizer.organisation.payments.status_action_body_due', { date: dueDate.value })
        : t('organizer.organisation.payments.status_action_body')
    case PayoutAccountStatus.Restricted:
      return t('organizer.organisation.payments.status_restricted_body')
    default:
      return null
  }
})

type Tone = 'good' | 'warn' | 'bad' | 'neutral'

const statusTone = computed<Tone>(() => {
  switch (status.value) {
    case PayoutAccountStatus.Active:
      return 'good'
    case PayoutAccountStatus.Restricted:
      return 'bad'
    case PayoutAccountStatus.ActionRequired:
    case PayoutAccountStatus.Incomplete:
      return 'warn'
    default:
      return 'neutral'
  }
})

const toneClasses: Record<Tone, string> = {
  good: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
  warn: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
  bad: 'bg-red-500/10 border-red-500/30 text-red-300',
  neutral: 'bg-slate-700/40 border-slate-600 text-slate-300',
}

const showFullExplanation = computed<boolean>(() => status.value === PayoutAccountStatus.NotStarted)
const payoutsPaused = computed<boolean>(
  () => status.value === PayoutAccountStatus.Active && account.value.payouts_enabled === false,
)

interface PrimaryAction {
  label: string
  kind: 'flow' | 'refresh'
  purpose?: PayoutLinkPurpose
  secondary: boolean
}

const primaryAction = computed<PrimaryAction>(() => {
  switch (status.value) {
    case PayoutAccountStatus.NotStarted:
      return {
        label: t('organizer.organisation.payments.start_button'),
        kind: 'flow',
        purpose: PayoutLinkPurpose.Onboarding,
        secondary: false,
      }
    case PayoutAccountStatus.Incomplete:
      return {
        label: t('organizer.organisation.payments.continue_button'),
        kind: 'flow',
        purpose: PayoutLinkPurpose.Onboarding,
        secondary: false,
      }
    case PayoutAccountStatus.PendingVerification:
      return { label: t('organizer.organisation.payments.check_status_button'), kind: 'refresh', secondary: false }
    case PayoutAccountStatus.Active:
      return {
        label: t('organizer.organisation.payments.update_existing_button'),
        kind: 'flow',
        purpose: PayoutLinkPurpose.Update,
        secondary: true,
      }
    default:
      return {
        label: t('organizer.organisation.payments.update_button'),
        kind: 'flow',
        purpose: PayoutLinkPurpose.Update,
        secondary: false,
      }
  }
})

const errorMessage = computed<string | null>(() => {
  const kind = flowErrorKind.value
  if (kind === null) return null
  if (kind === 'provider') return t('organizer.organisation.payments.error_provider')
  if (kind === 'forbidden') return t('organizer.organisation.payments.non_owner_message')
  return t('common.error_generic')
})

async function runFlow(purpose: PayoutLinkPurpose): Promise<void> {
  retryPurpose.value = purpose
  phase.value = 'idle'
  flowErrorKind.value = null
  const ok = await startHostedFlow(purpose)
  if (!ok) {
    flowErrorKind.value = flowError.value ?? 'generic'
    phase.value = 'error'
  }
}

async function runRefresh(): Promise<void> {
  retryPurpose.value = null
  flowErrorKind.value = null
  phase.value = 'idle'
  const result = await refresh()
  if (result === null) {
    flowErrorKind.value = flowError.value ?? 'generic'
    phase.value = 'error'
  }
}

async function handlePrimary(): Promise<void> {
  const action = primaryAction.value
  if (action.kind === 'refresh' || action.purpose === undefined) {
    await runRefresh()
    return
  }
  await runFlow(action.purpose)
}

async function retry(): Promise<void> {
  if (retryPurpose.value !== null) {
    await runFlow(retryPurpose.value)
  } else {
    await runRefresh()
  }
}

async function stripFlowQuery(): Promise<void> {
  const { payout_flow: _flow, purpose: _purpose, ...rest } = route.query
  await router.replace({ query: rest })
}

onMounted(async () => {
  if (!isOrgOwner.value) return

  const flow = route.query['payout_flow']
  const purposeParam = route.query['purpose']

  if (flow === 'return') {
    phase.value = 'returning'
    await stripFlowQuery()
    const result = await refresh()
    if (result === null) {
      flowErrorKind.value = flowError.value ?? 'generic'
      phase.value = 'error'
    } else {
      phase.value = 'idle'
    }
    return
  }

  if (flow === 'refresh') {
    const purpose =
      purposeParam === PayoutLinkPurpose.Update ? PayoutLinkPurpose.Update : PayoutLinkPurpose.Onboarding
    await stripFlowQuery()
    // One automatic fresh link per page load: a second failure shows the error
    // state instead of bouncing the owner between the app and the provider.
    if (!claimRefreshRedirect()) {
      flowErrorKind.value = 'generic'
      retryPurpose.value = purpose
      phase.value = 'error'
      return
    }
    await runFlow(purpose)
    return
  }

  // Keep the tab current: one silent background refresh per mount. Failures are
  // ignored and the stored state stays on screen.
  if (status.value !== PayoutAccountStatus.NotStarted) {
    await refresh()
  }
})
</script>

<template>
  <div class="rounded-2xl border border-slate-700 bg-slate-800/50 p-6">
    <h2 class="text-sm font-semibold text-slate-300 uppercase tracking-wide mb-6">
      {{ t('organizer.organisation.payments.heading') }}
    </h2>

    <!-- Returning from the hosted flow -->
    <div
      v-if="phase === 'returning'"
      class="flex items-center gap-3"
      role="status"
      data-testid="payments-returning"
    >
      <AppSpinner size="button" color="text-emerald-400" />
      <span class="text-sm text-slate-300">{{ t('organizer.organisation.payments.returning') }}</span>
    </div>

    <!-- Redirecting to the hosted page -->
    <div
      v-else-if="redirecting"
      class="flex items-center gap-3"
      role="status"
      data-testid="payments-redirecting"
    >
      <AppSpinner size="button" color="text-emerald-400" />
      <span class="text-sm text-slate-300">{{ t('organizer.organisation.payments.redirecting') }}</span>
    </div>

    <template v-else>
      <!-- Status block (shown to every member) -->
      <div
        class="rounded-xl border px-5 py-4 mb-6"
        :class="toneClasses[statusTone]"
        role="status"
        data-testid="payments-status"
        :data-status="status"
      >
        <p class="text-sm font-semibold">{{ statusTitle }}</p>
        <p v-if="statusBody && isOrgOwner" class="mt-1 text-sm opacity-90">{{ statusBody }}</p>
      </div>

      <!-- Non-owner: read-only -->
      <div
        v-if="!isOrgOwner"
        class="rounded-xl bg-slate-700/40 border border-slate-600 px-5 py-4 text-sm text-slate-400"
        data-testid="payments-non-owner"
      >
        {{ t('organizer.organisation.payments.non_owner_message') }}
      </div>

      <template v-else>
        <p
          v-if="payoutsPaused"
          class="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 text-sm text-amber-300"
          data-testid="payments-paused-note"
        >
          {{ t('organizer.organisation.payments.payouts_paused_note') }}
        </p>

        <!-- Error -->
        <div
          v-if="errorMessage"
          class="mb-6 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-4"
          role="alert"
          data-testid="payments-error"
        >
          <p class="text-sm text-red-400">{{ errorMessage }}</p>
          <button
            type="button"
            :disabled="busy"
            class="mt-3 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 font-semibold rounded-lg px-4 py-2 text-sm border border-slate-600 transition-colors"
            data-testid="payments-retry"
            @click="retry"
          >
            {{ t('organizer.organisation.payments.retry_button') }}
          </button>
        </div>

        <!-- Full explanation for owners who have not started -->
        <div v-if="showFullExplanation" class="mb-8">
          <PayoutExplanation />
        </div>

        <!-- Action -->
        <div class="flex flex-wrap items-center gap-4">
          <button
            type="button"
            :disabled="busy"
            :aria-describedby="primaryAction.kind === 'flow' ? 'payments-redirect-note' : undefined"
            class="inline-flex items-center gap-2 font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :class="
              primaryAction.secondary
                ? 'bg-slate-700 hover:bg-slate-600 text-slate-200 border border-slate-600'
                : 'bg-emerald-500 hover:bg-emerald-400 text-white'
            "
            data-testid="payments-primary-action"
            @click="handlePrimary"
          >
            <AppSpinner v-if="refreshing" size="button" />
            {{ primaryAction.label }}
          </button>

          <RouterLink
            v-if="status === PayoutAccountStatus.Active"
            :to="{ name: 'organizer.finance', query: { tab: 'payments' } }"
            class="text-sm font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
            data-testid="payments-view-payouts"
          >
            {{ t('organizer.organisation.payments.view_payouts_link') }}
          </RouterLink>
        </div>

        <p
          v-if="primaryAction.kind === 'flow'"
          id="payments-redirect-note"
          class="mt-3 text-xs text-slate-500"
        >
          {{
            status === PayoutAccountStatus.Active
              ? t('organizer.organisation.payments.update_existing_hint')
              : t('organizer.organisation.payments.redirect_note')
          }}
        </p>

        <!-- Collapsed explanation for every other state -->
        <details v-if="!showFullExplanation" class="mt-8" data-testid="payments-why">
          <summary class="cursor-pointer text-sm font-semibold text-slate-300 hover:text-white">
            {{ t('organizer.organisation.payments.why_toggle') }}
          </summary>
          <div class="mt-4">
            <PayoutExplanation />
          </div>
        </details>
      </template>
    </template>
  </div>
</template>
